import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Box, CloseButton, Dialog, HStack, IconButton, Portal, Separator, Spinner, Stack, VStack } from "@chakra-ui/react"
import { LuRefreshCw, LuClock, LuInfo, LuLandmark } from "react-icons/lu"
import { TiWarningOutline } from "react-icons/ti"
import { FaCheckCircle } from "react-icons/fa"
import { Button, Section, Text } from "@/components"
import { useAuth } from "@/contexts"
import { useClientContext } from "@/hooks"
import { Deal, DealStep, OfferStatus } from "@/types"
import { getCashFlowsByDealId, CashFlow, getPaymentsClientByEmail, listPaymentsBankAccounts, PaymentsBankAccount, PaymentsClient, disconnectPaymentsBank, getPaymentsByClientId, PaymentRecord, PaymentsApiError, requestWireEntry, cancelWireEntry, getAccountChangeCheck, AccountChangeCheck } from "@/api/payments"
import { ensurePaymentsClient } from "@/features/compliance/utils"
import { ConfettiBackground } from "@/components/ui/ConfettiBackground"
import { BankAccountSelector } from "./components/BankAccountSelector"
import { DealSummarySection } from "./components/DealSummarySection"
import { DmSubmitPanel } from "./components/DmSubmitPanel"
import { IrrReferencePanel } from "./components/IrrReferencePanel"
import { PaymentLedgerSection } from "./components/PaymentLedgerSection"
import {
  UsPlaidWireInformationComponent,
  ManualBankFormComponent,
} from "@/features/compliance/components"

// ── Test overrides ──
const TEST_EMAIL = "williamskyu@gmail.com"

// Mock deal used when the test user has no deals in the database
const MOCK_DEAL: Deal = {
  id: 0,
  company: { id: 0, name: "Test Company Inc.", fundingRounds: [] },
  offers: [
    {
      id: 0,
      exerciseCost: 0,
      desiredLiquidity: 150,      // $1.50
      pp: 0.50,
      lp: 0,
      isActive: 1,
      isAccepted: 1,
      isIncentive: 0,
      incentivePp: null,
      incentiveLp: null,
      incentiveDeadline: null,
      incentiveType: null,
      hypotheticalSharePrice: 1000,
      equities: [],
      offerNumber: 1,
    },
  ],
  dealManager: { id: 0, userType: "deal_manager", firstName: "Test", lastName: "DM", email: "dm@test.com", profilePicture: "", phoneNumber: "", calendly: "" },
  stage: OfferStatus.POST_CLOSING,
  client: { id: 0, userType: "client", firstName: "Bilbo", lastName: "Yu", email: TEST_EMAIL, createdDate: "", deals: [], dealManager: { id: 0, userType: "deal_manager", firstName: "Test", lastName: "DM", email: "dm@test.com", profilePicture: "", phoneNumber: "", calendly: "" } },
  isActive: 1,
  currentStep: DealStep.CLOSING,
  pendingAmount: 0,
}

// Second mock deal — liquidity payment test
const MOCK_DEAL_LIQUIDITY: Deal = {
  ...MOCK_DEAL,
  id: 1,
  company: { id: 1, name: "Test Company Inc. 2", fundingRounds: [] },
  offers: [
    {
      ...MOCK_DEAL.offers[0],
      id: 1,
      exerciseCost: null as unknown as number,
      desiredLiquidity: 150,      // $1.50
    },
  ],
  pendingAmount: 1.50,
}

const MOCK_DEAL_IDS = [0, 1]

// Per-browser conveniences only (the server holds the real state). Every access is
// guarded: storage can be blocked, full, or missing in private windows.
const readFlag = (key: string): boolean => {
  try { return typeof window !== "undefined" && window.localStorage?.getItem(key) === "1" } catch { return false }
}
const writeFlag = (key: string, on: boolean) => {
  try {
    if (typeof window === "undefined" || !window.localStorage) return
    if (on) window.localStorage.setItem(key, "1")
    else window.localStorage.removeItem(key)
  } catch { /* ignore */ }
}

type PaymentMethod = "us" | "foreign"
type UsSubMethod = "plaid" | "manual" | null

interface Props {
  isLoading: boolean
  deals: Deal[]
  onRefresh?: () => void
}

export const PaymentCenterComponent = ({ deals, isLoading, onRefresh }: Props) => {
  const { user } = useAuth()
  // Identity of the CLIENT whose Payment Center this is — the logged-in client,
  // or the viewed client when a deal manager is in view-as mode. Never the raw
  // logged-in user: reading the DM's identity here caused the spurious
  // "Client ID Error" and looked up the DM's own email on the payments server.
  const clientContext = useClientContext()
  const isViewingAsClient = clientContext?.isViewingAsClient ?? false
  const [currentDeal, setCurrentDeal] = useState<Deal | null>(null)
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethod | null>(null)
  const [usSubMethod, setUsSubMethod] = useState<UsSubMethod>(null)
  const [cashFlows, setCashFlows] = useState<CashFlow[]>([])
  const [cashFlowsLoading, setCashFlowsLoading] = useState(false)
  const [connectedBankAccounts, setConnectedBankAccounts] = useState<PaymentsBankAccount[]>([])
  const [bankAccountsLoading, setBankAccountsLoading] = useState(false)
  const [paymentsClientCountry, setPaymentsClientCountry] = useState<string | null>(null)
  // The legal name on the payments client record, the manual form's locked account
  // title when set (Bill, 2026-09-29).
  const [paymentsClientLegalName, setPaymentsClientLegalName] = useState<string | null>(null)
  const [paymentsClientId, setPaymentsClientId] = useState<string | null>(null)
  const [clientIdError, setClientIdError] = useState(false)
  const [isClearing, setIsClearing] = useState(false)
  const [showClearModal, setShowClearModal] = useState(false)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [successMessage, setSuccessMessage] = useState("")
  const [successTitle, setSuccessTitle] = useState("Payment Submitted!")
  const [dataCleared, setDataCleared] = useState(false)
  // Why this is surfaced instead of logged: the disconnect POST hit a route
  // that did not exist on the portal backend from 2026-04-06 until it was
  // added, and because the failure only reached console.error the button
  // looked like it worked. A client clicked, the modal closed, the bank
  // stayed. Never let this path fail silently again.
  const [disconnectError, setDisconnectError] = useState<string | null>(null)
  // "Change Bank Account": renders the normal connect flow in replace mode while a
  // bank is already connected. The server owns the semantics (deactivate siblings,
  // 409 while a payment is approved/sent, re-point a pending one) — this flag only
  // decides whether the form is shown. Before 2026-09-24 the only way to reach the
  // form with a bank connected was a DM-issued Client Override, which made the
  // highest-stakes moment (money heading to a wrong account) an email round-trip.
  const [replaceMode, setReplaceMode] = useState(false)
  const [paymentHistory, setPaymentHistory] = useState<PaymentRecord[]>([])
  // Manual-bank fallback unlock — hidden by default for US clients, surfaced
  // after Plaid Link reports an error (e.g. Schwab accounts that don't link
  // cleanly). Persisted in localStorage so it sticks across reloads.
  const [manualBankUnlocked, setManualBankUnlocked] = useState(false)
  // Seeds the bank-country selector inside ManualBankFormComponent when the client
  // reaches manual entry via the "bank outside the US" escape hatch. Empty string
  // means "no hint" and the form falls back to its own detection, then to US.
  const [manualCountryHint, setManualCountryHint] = useState("")
  // Set when the client reaches manual entry by declaring their bank is not in the
  // US: the form then starts with no country selected instead of defaulting to US.
  const [promptForCountry, setPromptForCountry] = useState(false)
  const [showManualUnlockModal, setShowManualUnlockModal] = useState(false)
  const [manualUnlockReason, setManualUnlockReason] = useState("")
  // "Receive Payments by Wire" (Bill, 2026-09-29, after the Verzosa / M1 Finance
  // payment went out by ACH). When the connected account can only be paid by ACH
  // (a Plaid limitation: Plaid gave us no wire details for it), the client gets a
  // one-time popup and a persistent button to enter their bank's wire
  // instructions. Asking flips the same Client Override switch a DM's link does
  // (the backend checks the account really is ACH-only and emails the DM), and
  // "Cancel and Keep ACH" turns it back off.
  const [wireOfferOpen, setWireOfferOpen] = useState(false)
  const [wireRequestLoading, setWireRequestLoading] = useState(false)
  const [wireRequestError, setWireRequestError] = useState<string | null>(null)
  const [wireEntryRequested, setWireEntryRequested] = useState(false)
  const [wireCancelLoading, setWireCancelLoading] = useState(false)
  // Set when the client cancels: the session's manualEntryEnabled is still 1
  // until the next /auth/me, so the form must close from here.
  const [overrideClosed, setOverrideClosed] = useState(false)
  const bankFormRef = useRef<HTMLDivElement | null>(null)

  const isTestUser = (clientContext?.email ?? user?.email)?.toLowerCase().trim() === TEST_EMAIL

  // For test user, always include mock deals alongside real deals
  const effectiveDeals = useMemo(() => {
    if (isTestUser) {
      return [...(deals || []), MOCK_DEAL, MOCK_DEAL_LIQUIDITY]
    }
    return deals
  }, [deals, isTestUser])

  const sortedDeals = useMemo(() => [...effectiveDeals].sort((a, b) => b.id - a.id), [effectiveDeals])

  useEffect(() => {
    if (effectiveDeals && effectiveDeals.length > 0) {
      // Money staged wins. Otherwise: the most recent OPEN deal, never simply
      // effectiveDeals[0] — /deals/client returns inactive deals too, oldest
      // first, so a multi-deal client defaulted to a DEAD deal. That is how the
      // bank-connect/disconnect emails for Zeeshan Yoonas went to the DM of his
      // passed Netlify deal while Nairie, working his live Scribe deal, heard
      // nothing (Bill, 2026-09-24: "default to the most recent DM with an open
      // deal"). Everything on this page keys off currentDeal — the deal summary,
      // the deal_eso_id sent with connect/disconnect, and therefore which DM is
      // notified.
      const dealWithPending = effectiveDeals.find(d => (d.pendingAmount ?? 0) > 0)
      const mostRecentOpen = effectiveDeals
        .filter(d => d.isActive === 1)
        .sort((a, b) => (b.esoId ?? b.id) - (a.esoId ?? a.id))[0]
      setCurrentDeal(dealWithPending ?? mostRecentOpen ?? effectiveDeals[0])
    }
  }, [effectiveDeals])

  // Fetch cash flows when deal changes (skip mock deals)
  useEffect(() => {
    if (!currentDeal) return
    if (MOCK_DEAL_IDS.includes(currentDeal.id)) {
      setCashFlows([])
      setCashFlowsLoading(false)
      return
    }
    setCashFlowsLoading(true)
    getCashFlowsByDealId(String(currentDeal.id))
      .then(data => { setCashFlows(data) })
      .catch(() => { setCashFlows([]) })
      .finally(() => { setCashFlowsLoading(false) })
  }, [currentDeal])

  // Ensure client exists on payments server and fetch bank accounts (client-level, not deal-level).
  // Bank accounts persist across all deals for the same client, so we only fetch once on load
  // and after payment success — not on every deal switch.
  const hasFetchedClient = useRef(false)

  useEffect(() => {
    if (!clientContext?.email || !clientContext?.firstName || !clientContext?.lastName) return
    if (!currentDeal?.company?.name) return
    if (hasFetchedClient.current) return
    hasFetchedClient.current = true

    const clientEmail = clientContext.email

    setBankAccountsLoading(true)

    const doFetch = async () => {
      // Step 1: Push portal client data to payments server (only fields the portal actually has)
      let paymentsClient: PaymentsClient | null = null

      // client_number is required — test user uses ESO-0, real clients must have an esoId
      const esoId = clientContext.esoId
      if (!esoId && !isTestUser) {
        setClientIdError(true)
        paymentsClient = await getPaymentsClientByEmail(clientEmail).catch(() => null)
      } else {
        setClientIdError(false)
        if (isViewingAsClient) {
          // View-as is read-only against the payments server: look the client
          // up, never create/update their record from a DM session.
          paymentsClient = await getPaymentsClientByEmail(clientEmail).catch(() => null)
        } else {
          try {
            paymentsClient = await ensurePaymentsClient({
              first_name: clientContext.firstName,
              last_name: clientContext.lastName,
              email: clientEmail,
              company_name: currentDeal.company.name,
              client_number: esoId ? String(esoId) : "0",
              // Note: address/city/state/zip/country are NOT in the portal DB.
              // They are collected by the payment forms and stored on the payments server.
              // We omit them here so we don't overwrite existing values with undefined.
            })
          } catch {
            // If push fails, try a simple lookup — client may already exist
            paymentsClient = await getPaymentsClientByEmail(clientEmail).catch(() => null)
          }
        }
      }

      if (!paymentsClient) {
        setPaymentsClientCountry(null)
        setPaymentsClientLegalName(null)
        setPaymentsClientId(null)
        setConnectedBankAccounts([])
        return
      }

      // Step 2: Pull country + bank accounts + payment history from payments server
      setPaymentsClientId(paymentsClient.id)
      setPaymentsClientCountry(paymentsClient.country ?? null)
      setPaymentsClientLegalName(paymentsClient.legal_name ?? null)
      // Restore manual-bank unlock for this client if they previously hit a
      // Plaid failure on a prior visit.
      try {
        if (typeof window !== "undefined" && window.localStorage) {
          const unlocked = window.localStorage.getItem(`plaid-failure-unlock-${paymentsClient.id}`) === "1"
          if (unlocked) setManualBankUnlocked(true)
        }
      } catch { /* ignore localStorage errors */ }
      try {
        const accounts = await listPaymentsBankAccounts(paymentsClient.id)
        setConnectedBankAccounts(accounts)
      } catch {
        setConnectedBankAccounts([])
      }
      try {
        const history = await getPaymentsByClientId(paymentsClient.id)
        setPaymentHistory(history)
      } catch {
        setPaymentHistory([])
      }
    }

    doFetch()
      .catch(() => { setConnectedBankAccounts([]); setPaymentsClientCountry(null) })
      .finally(() => { setBankAccountsLoading(false) })
  }, [clientContext?.email, clientContext?.firstName, clientContext?.lastName, currentDeal?.company?.name, isViewingAsClient, isTestUser])

  // DM-enabled escape hatch: when the deal manager clicks "Enable Client Manual
  // Entry" (Bank Connected email) or "Client Override" (approval email), the
  // server flips client.manualEntryEnabled. Surface the manual bank-entry tab
  // without requiring a Plaid failure first, and open it directly — the switch
  // exists precisely so the client can (re-)enter details manually.
  useEffect(() => {
    if (clientContext?.manualEntryEnabled === 1) {
      setManualBankUnlocked(true)
      setUsSubMethod("manual")
    }
  }, [clientContext?.manualEntryEnabled])

  // Client Override (server switch): also show the connection forms when a bank
  // is ALREADY connected (or everything is paid) so wrong bank details can be
  // REPLACED — a successful manual save deactivates the old account, re-points a
  // pending payment, and consumes this switch (portal-backend clears the flag).
  const manualEntryOverride = clientContext?.manualEntryEnabled === 1 && !overrideClosed

  // Derive foreign status from payments client country (no DB migration needed)
  const US_COUNTRY_VALUES = ["US", "USA", "UNITED STATES", "UNITED STATES OF AMERICA"]
  const isForeign = paymentsClientCountry != null
    && paymentsClientCountry.trim() !== ""
    && !US_COUNTRY_VALUES.includes(paymentsClientCountry.trim().toUpperCase())

  // Auto-select payment method based on country (non-test users)
  useEffect(() => {
    if (isTestUser) return // test user picks manually
    setSelectedMethod(isForeign ? "foreign" : "us")
  }, [isForeign, isTestUser])

  // Client data available from the portal DB — address fields are NOT stored here,
  // they live on the payments server and are collected by the payment forms directly.
  // Always the CLIENT's identity (see clientContext above), never the logged-in DM's.
  const defaultClientData = useMemo(() => ({
    firstName: clientContext?.firstName ?? "",
    lastName: clientContext?.lastName ?? "",
    email: clientContext?.email ?? "",
    companyName: currentDeal?.company?.name ?? "",
    esoId: clientContext?.esoId ?? undefined,
    phoneNumber: clientContext?.phoneNumber ?? undefined,
    legalName: paymentsClientLegalName,
  }), [clientContext?.firstName, clientContext?.lastName, clientContext?.email, currentDeal?.company?.name, clientContext?.esoId, clientContext?.phoneNumber, paymentsClientLegalName])

  // Determine if bank is already connected
  const hasConnectedBank = connectedBankAccounts.length > 0
  const activeBank = connectedBankAccounts[0]
  const connectedBankName = activeBank?.bank_name
  // How payments to it will ACTUALLY go (the payments server's rail decision);
  // the stored transfer_type can say WIRE for an account that only takes ACH.
  const connectedBankType = activeBank?.effective_rail ?? activeBank?.transfer_type

  // ── Determine payment status ──
  const releasedTotal = paymentHistory
    .filter(p => p.status === "RELEASED")
    .reduce((sum, p) => sum + p.amount, 0)
  const totalPendingLiquidity = effectiveDeals.reduce((sum, d) => sum + (d.pendingAmount ?? 0), 0)

  // ONE PAYMENT MAPS TO ONE DEAL. This is the amount handed to the bank-connect
  // forms, and it goes out alongside dealEsoId={currentDeal.esoId} — so it must
  // be THIS deal's staged amount, never the client-wide sum.
  //
  // It was totalPendingLiquidity. With two staged deals that created a single
  // transaction for the combined total tagged to one deal; on release the
  // portal credited the whole sum to that one deal (markDealReleased takes one
  // esoId), pushing it past its own total while the other deal kept a pending
  // amount with no transaction behind it. Latent — only two clients have ever
  // had more than one money-carrying deal, neither staged at once — but it
  // silently misstates the ledger the first time it happens.
  //
  // totalPendingLiquidity is still right for the client-wide views below
  // (isAllPaid, the disconnect lock); it is only wrong as a payment amount.
  const paymentAmount = currentDeal?.pendingAmount ?? 0
  const paymentLabel = releasedTotal > 0 ? "Payments Released from ESO Fund" : "Pending Payment from ESO Fund"

  const handleClearTestData = useCallback(async () => {
    if (!paymentsClientId) return
    setIsClearing(true)
    setDisconnectError(null)
    try {
      const result = await disconnectPaymentsBank(paymentsClientId, currentDeal?.esoId ?? undefined)
      // Report what actually happened. The server deliberately leaves third-party
      // beneficiary accounts alone (ESO sets those up for the deal; "disconnect my
      // bank" must not dismantle one), so a 200 does not by itself mean an account
      // was removed. Clearing the UI on a no-op would show "Bank account
      // disconnected" over an account that is still the live payout target, and a
      // refresh would bring it back.
      if ((result?.accounts_deactivated ?? 0) === 0) {
        // Nothing was removed. In practice that means a duplicate submit whose first
        // call already worked; the other cause is a third-party beneficiary account,
        // which the server deliberately leaves alone. Neither deserves an error —
        // telling someone whose disconnect just succeeded to call their equity
        // partner is worse than saying nothing. Re-read the list and let it speak
        // for itself. The one thing we must not do is clear the UI, which would
        // claim a disconnect that did not happen.
        //
        // Refetched inline rather than via refreshPaymentData: that callback is
        // declared below this one, so naming it in this hook's dependency array
        // would be read during render and hit the temporal dead zone.
        try {
          setConnectedBankAccounts(await listPaymentsBankAccounts(paymentsClientId))
        } catch { /* leave the list as it was */ }
        return
      }
      setConnectedBankAccounts([])
      // Re-select rather than clearing: the auto-routing effect keys on
      // [isForeign, isTestUser], neither of which changes on a disconnect, so it
      // will not re-run. Setting null here dropped a foreign client onto the US
      // Plaid form - which cannot connect a non-US bank at all.
      setSelectedMethod(isForeign ? "foreign" : "us")
      setDataCleared(true)
      // Note: paymentsClientId and paymentHistory are intentionally NOT cleared —
      // payment history persists even after disconnecting a bank account
    } catch (err) {
      console.error("[payments] Disconnect failed:", err)
      setDisconnectError(
        err instanceof PaymentsApiError && err.status >= 400 && err.status !== 404 && err.message
          ? err.message
          : "We couldn't disconnect your bank account. Please try again, or contact your equity partner.",
      )
    } finally {
      setIsClearing(false)
      setShowClearModal(false)
    }
  }, [paymentsClientId, currentDeal, isForeign])

  // Whether a payment for this client is already in progress (PENDING or COMPLETED
  // but not yet released). Clients no longer accept payments — submission is
  // automatic (OG request auto-submits when a bank is connected; connecting a
  // bank submits the pending amount) — so this just drives the status text.
  const hasPendingPayment = paymentHistory.some(p => p.status === "PENDING" || p.status === "COMPLETED")
  // COMPLETED = the payments server's 'sent': the file may already be at Stifel.
  // PENDING covers both pending_approval (server re-points it on a replace) and
  // approved (server 409s with a readable message) — indistinguishable here, so
  // the server is the judge for those. Only 'sent' is gated client-side.
  const hasSentPayment = paymentHistory.some(p => p.status === "COMPLETED")

  // What a bank-account change would do to the client's payments in flight (payments
  // server, Bill 2026-10-13): one approved or on its way locks the change until it is
  // released (or Mike cancels it); one waiting for approval is cancelled and resubmitted
  // once the client confirms. The history's PENDING cannot tell those two apart, so ask
  // the server, again whenever the history refreshes. null (no answer) falls back to the
  // old rule, where only a sent payment locks and the server refuses the rest itself.
  const [accountCheck, setAccountCheck] = useState<AccountChangeCheck | null>(null)
  useEffect(() => {
    if (!paymentsClientId) return
    let live = true
    void getAccountChangeCheck(paymentsClientId).then((check) => { if (live) setAccountCheck(check) })
    return () => { live = false }
  }, [paymentsClientId, paymentHistory])
  const changeLocked = accountCheck ? !!accountCheck.in_process : hasSentPayment

  // ACH-only connected account: the server's rail check says no wire upgrade is
  // available (null = it couldn't tell, which offers nothing). Not offered while a
  // payment is on its way (it can't be re-pointed) or in DM view-as.
  const achOnlyBank = !!activeBank && activeBank.wire_upgrade_available === false
  const canOfferWire = achOnlyBank && !changeLocked && !isViewingAsClient
  const wireRequestedKey = paymentsClientId ? `wire-entry-requested-${paymentsClientId}` : ""
  // Wire-worded form only for an override the client opened for a wire. A DM's
  // Client Override on the same account keeps the ordinary form (it may be there to
  // fix a wrong account, and require_wire would refuse an ACH-only routing).
  const wireEntryMode = wireEntryRequested || (manualEntryOverride && achOnlyBank && !!wireRequestedKey && readFlag(wireRequestedKey))
  const wireOfferShown = canOfferWire && !manualEntryOverride && !wireEntryRequested
  const wireOfferBankLabel = activeBank
    ? `${activeBank.bank_name ?? "bank"} account${activeBank.plaid_mask ? ` ending in ${activeBank.plaid_mask}` : ""}`
    : "bank account"

  // Lock "Disconnect & Clear" while there's still money owed (outstanding) OR
  // sitting in pending/in-flight. Re-enables once the deal is fully paid out
  // (outstanding === 0 AND no pending AND no in-flight transaction). Payment
  // history stays attached to the client (server-side keys on client_id, not
  // bank_account_id) so a future reconnect keeps the history.
  const outstandingDollars = effectiveDeals.reduce((sum, d) => sum + (d.outstandingAmount ?? 0), 0)
  const cantDisconnectBank = outstandingDollars > 0 || totalPendingLiquidity > 0 || hasPendingPayment
  const cantDisconnectReason = outstandingDollars > 0
    ? `You still have ${new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(outstandingDollars)} in outstanding payments across your deals. You can disconnect once all deals are fully paid out.`
    : totalPendingLiquidity > 0
      ? "You have a payment pending ESO Fund approval. You can disconnect once it's released."
      : "A payment is in flight to your bank. You can disconnect once it's released."


  // Refresh payment history and bank accounts so the UI updates immediately
  const refreshPaymentData = useCallback(async () => {
    if (!paymentsClientId) return
    setDisconnectError(null)
    try {
      const history = await getPaymentsByClientId(paymentsClientId)
      setPaymentHistory(history)
    } catch { /* ignore */ }
    try {
      const accounts = await listPaymentsBankAccounts(paymentsClientId)
      setConnectedBankAccounts(accounts)
    } catch { /* ignore */ }
  }, [paymentsClientId])

  const handlePaymentSuccess = useCallback(async (message: string) => {
    // The wire entry (if any) is done: the saved account replaced the ACH one.
    const wasWireEntry = wireEntryRequested || (!!paymentsClientId && readFlag(`wire-entry-requested-${paymentsClientId}`))
    if (paymentsClientId) writeFlag(`wire-entry-requested-${paymentsClientId}`, false)
    await refreshPaymentData()
    setSuccessTitle(wasWireEntry ? "Wire Instructions Saved" : "Bank Connected!")
    setSuccessMessage(message)
    setShowSuccessModal(true)
  }, [refreshPaymentData, paymentsClientId, wireEntryRequested])

  const handleSuccessModalClose = useCallback(() => {
    setShowSuccessModal(false)
    window.location.reload()
  }, [])

  // Called by UsPlaidWireInformationComponent when Plaid Link exits with an
  // error. Persist the unlock to localStorage so the manual-bank button stays
  // available across reloads, and pop the explanatory modal once.
  const handlePlaidFailure = useCallback((reason: string) => {
    if (manualBankUnlocked) return // already unlocked, don't re-pop the modal
    setManualUnlockReason(reason)
    setManualBankUnlocked(true)
    setShowManualUnlockModal(true)
    try {
      if (paymentsClientId && typeof window !== "undefined" && window.localStorage) {
        window.localStorage.setItem(`plaid-failure-unlock-${paymentsClientId}`, "1")
      }
    } catch { /* ignore localStorage errors */ }
  }, [manualBankUnlocked, paymentsClientId])

  // "Receive Payments by Wire": open the manual form for wire entry.
  const handleRequestWireEntry = useCallback(async () => {
    if (!paymentsClientId) return
    setWireRequestLoading(true)
    setWireRequestError(null)
    try {
      await requestWireEntry(paymentsClientId)
      writeFlag(`wire-entry-requested-${paymentsClientId}`, true)
      if (activeBank?.id) writeFlag(`wire-offer-seen-${activeBank.id}`, true)
      setWireEntryRequested(true)
      setManualBankUnlocked(true)
      setSelectedMethod("us")
      setUsSubMethod("manual")
      setWireOfferOpen(false)
    } catch (err) {
      setWireRequestError(
        err instanceof PaymentsApiError && err.status >= 400 && err.status < 500 && err.message
          ? err.message
          : "We were unable to start wire setup. Please try again, or contact your equity partner.",
      )
    } finally {
      setWireRequestLoading(false)
    }
  }, [paymentsClientId, activeBank?.id])

  // "Cancel and Keep ACH": close a wire entry the client opened. The connected ACH
  // account never changed, so there is nothing else to undo.
  const handleCancelWireEntry = useCallback(async () => {
    if (!paymentsClientId) return
    setWireCancelLoading(true)
    setWireRequestError(null)
    try {
      await cancelWireEntry(paymentsClientId)
      writeFlag(`wire-entry-requested-${paymentsClientId}`, false)
      setWireEntryRequested(false)
      setOverrideClosed(true)
      setUsSubMethod(null)
    } catch (err) {
      setWireRequestError(
        err instanceof PaymentsApiError && err.status >= 400 && err.status < 500 && err.message
          ? err.message
          : "We were unable to cancel wire setup. Please try again, or contact your equity partner.",
      )
    } finally {
      setWireCancelLoading(false)
    }
  }, [paymentsClientId])

  // Closing the popup either way means "seen": it appears once per account, and
  // the button in the bank box stays for whenever they change their mind.
  const dismissWireOffer = useCallback(() => {
    if (activeBank?.id) writeFlag(`wire-offer-seen-${activeBank.id}`, true)
    setWireOfferOpen(false)
    setWireRequestError(null)
  }, [activeBank?.id])

  useEffect(() => {
    if (!wireOfferShown || bankAccountsLoading || !activeBank?.id) return
    if (showSuccessModal || showManualUnlockModal || showClearModal) return
    if (readFlag(`wire-offer-seen-${activeBank.id}`)) return
    setWireOfferOpen(true)
  }, [wireOfferShown, bankAccountsLoading, activeBank?.id, showSuccessModal, showManualUnlockModal, showClearModal])

  // Bring the wire form into view once it opens.
  useEffect(() => {
    if (wireEntryRequested) bankFormRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
  }, [wireEntryRequested])

  // "Fully paid" must account for money the OG has sized but not yet staged.
  // Without the outstanding check, a client whose first tranche released and
  // whose second is sized-but-unstaged (pending = 0, outstanding > 0) reads as
  // fully paid: the "your next payment is being prepared" banner disappears and
  // an unconnected client loses the bank-connect form — the deal looks finished
  // to them while $X is still owed. (Winston Laoh / MeetElise 37209, 2026-07-30:
  // $38,359.98 released, $9,000 outstanding, page showed nothing pending.)
  const isAllPaid = totalPendingLiquidity <= 0 && outstandingDollars <= 0 && releasedTotal > 0

  // Persistent identity-mismatch banner: surfaces any connected bank account
  // whose Plaid identity check flagged a mismatch. Stays visible across page
  // loads (data lives on bank_account.identity_check) until the bank is
  // disconnected.
  const mismatchedBank = connectedBankAccounts.find(
    (b) => b.identity_check && b.identity_check.match === false,
  )

  return (
    <Section>
      <HStack>
        <Text fontSize="2xl" fontWeight="bold" fontFamily="body">
          Payment Center
        </Text>
        {onRefresh && <IconButton aria-label="Refresh" variant="ghost" size="sm" onClick={onRefresh}><LuRefreshCw /></IconButton>}
      </HStack>
      {mismatchedBank && mismatchedBank.identity_check && (
        <Stack mt={3} p={5} borderWidth="1px" borderRadius="lg" borderColor="orange.300" bg="orange.50" gap={2}>
          <Text fontSize="md" fontWeight="semibold" color="orange.800">
            Bank account owner doesn&rsquo;t fully match our records
          </Text>
          <Stack gap={1} mt={1}>
            {(["name", "email", "phone"] as const).map((field) => {
              const isMismatch = mismatchedBank.identity_check!.mismatches.includes(field)
              return (
                <Text key={field} fontSize="sm" color={isMismatch ? "red.700" : "green.700"}>
                  {isMismatch ? "\u2716" : "\u2714"}{" "}
                  <Text as="span" fontWeight="medium" textTransform="capitalize">{field}:</Text>{" "}
                  {isMismatch ? "doesn\u2019t match" : "matches"}
                </Text>
              )
            })}
          </Stack>
          <Text fontSize="sm" color="gray.700" mt={2}>
            We&rsquo;ve notified your equity partner by email to confirm whether to proceed. Your bank
            connection ({mismatchedBank.bank_name}) has been saved and any pending payment is awaiting standard approval.
          </Text>
          <Text fontSize="sm" color="gray.700">
            If you know why these don&rsquo;t match (e.g. joint account with a spouse, secondary email
            on file with the bank, or an old phone number), please reply to your equity partner with a
            short explanation so they can confirm faster.
          </Text>
        </Stack>
      )}
      {
        isLoading ? (
          <HStack w="100%" justifyContent="center" alignItems="center" p={10}>
            <Spinner />
          </HStack>
        ) : (
          <>
            {currentDeal && (
              <Stack gap={6} mt={5}>
                {/* Bank section — always at top */}
                {bankAccountsLoading && (
                  <HStack justifyContent="center" p={2}>
                    <Spinner size="sm" />
                    <Text fontSize="sm" color="gray.500">Checking bank connection...</Text>
                  </HStack>
                )}
                {!bankAccountsLoading && dataCleared && !hasConnectedBank && (
                  <Stack p={4} borderWidth="1px" borderRadius="lg" borderColor="blue.200" bg="blue.50">
                    <Text fontSize="sm" fontWeight="semibold" color="blue.700">
                      Bank account disconnected
                    </Text>
                    <Text fontSize="xs" color="gray.600">
                      It will no longer receive payments. Your payment history is unchanged. You can connect a different account below.
                    </Text>
                  </Stack>
                )}
                {!bankAccountsLoading && hasConnectedBank && (
                  <Stack p={4} borderWidth="1px" borderRadius="lg" borderColor="green.200" bg="green.50">
                    <HStack justifyContent="space-between" alignItems="center">
                      <Text fontSize="sm" fontWeight="semibold" color="green.700">
                        ✓ Bank Securely Connected
                      </Text>
                      {!isViewingAsClient && (
                        <HStack gap={2}>
                          <Button
                            variant="outline"
                            size="xs"
                            disabled={changeLocked || replaceMode}
                            onClick={() => setReplaceMode(true)}
                          >
                            Change Bank Account
                          </Button>
                          <Button
                            variant="outline"
                            size="xs"
                            colorPalette="red"
                            disabled={isClearing || cantDisconnectBank}
                            title={cantDisconnectBank ? cantDisconnectReason : undefined}
                            onClick={() => { setDisconnectError(null); setShowClearModal(true) }}
                          >
                            Disconnect & Clear
                          </Button>
                        </HStack>
                      )}
                    </HStack>
                    {/* The reason, as text a human can see. A title tooltip does not
                        render on touch and unreliably on a disabled element — a greyed
                        button with no explanation is how a working lock got reported
                        as a removed button. */}
                    {!isViewingAsClient && changeLocked && (
                      <Text fontSize="xs" color="gray.600">
                        A payment is on its way to this account. You can change or disconnect it once the payment is released.
                        If it needs to go to a different account, please contact your equity partner.
                      </Text>
                    )}
                    {!isViewingAsClient && !changeLocked && cantDisconnectBank && (
                      <Text fontSize="xs" color="gray.600">
                        {cantDisconnectReason} Need payments to go to a different account? Use Change Bank Account.
                      </Text>
                    )}

                    <Text fontSize="xs" color="gray.600">
                      {connectedBankName ?? "Bank account"} ({connectedBankType}) — Your payment details are encrypted and stored securely.
                    </Text>
                    {wireOfferShown && (
                      <Stack mt={1} p={3} borderWidth="1px" borderRadius="md" borderColor="orange.200" bg="orange.50" gap={2}>
                        {/* gray.800, not an orange shade: this theme's orange scale is
                            inverted (orange.100 darkest, orange.800 palest), so
                            orange.800 text is near-invisible on this background. */}
                        <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                          ACH Transfers Only
                        </Text>
                        <Text fontSize="xs" color="gray.700">
                          Plaid, the service we use to connect bank accounts, could provide only the ACH details
                          for this account. If you would prefer to receive your payments by wire, you can provide
                          your bank&rsquo;s wire instructions.
                        </Text>
                        <Button
                          size="xs"
                          variant="solid"
                          w="fit-content"
                          loading={wireRequestLoading}
                          disabled={wireRequestLoading}
                          onClick={handleRequestWireEntry}
                        >
                          Receive Payments by Wire
                        </Button>
                        {wireRequestError && !wireOfferOpen && (
                          <Text fontSize="xs" color="red.600">{wireRequestError}</Text>
                        )}
                      </Stack>
                    )}
                    {!hasPendingPayment && totalPendingLiquidity <= 0 && outstandingDollars <= 0 && (
                      <Text fontSize="xs" color="gray.500">
                        No payment is pending right now. When one is staged it will appear below and pay out to this account.
                      </Text>
                    )}
                    {/* Plaid items often expose several accounts; when they do, the
                        client can re-point which one receives payments (client-only —
                        view-as stays read-only; renders nothing for single-account
                        items and manual/foreign entries). */}
                    {!isViewingAsClient && paymentsClientId && connectedBankAccounts.some((a) => a.plaid_account_id) && (
                      <BankAccountSelector
                        paymentsClientId={paymentsClientId}
                        dealEsoId={currentDeal?.esoId ?? undefined}
                        hasPendingPayment={hasPendingPayment}
                        changeCheck={accountCheck}
                        onSwitched={refreshPaymentData}
                      />
                    )}
                  </Stack>
                )}

                {/* Outside the connected-bank box on purpose: a disconnect that fails or
                    no-ops must still be able to say so even when that box is not rendered. */}
                {disconnectError && (
                  <Text fontSize="sm" color="red.600" fontWeight="medium">
                    {disconnectError}
                  </Text>
                )}

                {/* View-as: say exactly what the CLIENT's screen shows and why. This
                    exists because a DM cannot act here (money actions are the client's
                    consent) — but "I can't see what they see" is how a working lock got
                    reported as a removed button (Zeeshan, 2026-09-23) and how a wrong
                    default deal went unnoticed (Vlad, 2026-09-24). The DM answers the
                    client's question in one glance instead of guessing. */}
                {isViewingAsClient && !bankAccountsLoading && (
                  <Stack p={4} borderWidth="1px" borderRadius="lg" borderColor="purple.200" bg="purple.50" gap={1}>
                    <Text fontSize="sm" fontWeight="semibold" color="purple.900">
                      What the client sees (read-only)
                    </Text>
                    {hasConnectedBank ? (
                      <>
                        <Text fontSize="xs" color="gray.700">
                          Bank connected: {connectedBankName ?? "Bank account"} ({connectedBankType})
                        </Text>
                        <Text fontSize="xs" color="gray.700">
                          Change Bank Account: {changeLocked
                            ? "locked — a payment is approved or on its way to the current account (Mike can cancel it if it must go elsewhere)"
                            : accountCheck?.waiting
                              ? "available — their payment waiting for approval would be cancelled and resubmitted to the new account, once they confirm"
                              : "available"}
                        </Text>
                        <Text fontSize="xs" color="gray.700">
                          Disconnect: {cantDisconnectBank ? `locked — ${cantDisconnectReason}` : "available"}
                        </Text>
                        {achOnlyBank && (
                          <Text fontSize="xs" color="gray.700">
                            Wire: not available on this account ({activeBank?.wire_unavailable_reason === "tokenized_account"
                              ? "Plaid gave a tokenized, ACH-only account number"
                              : "its routing accepts ACH only and Plaid gave no wire routing"}).{" "}
                            {changeLocked
                              ? "The Receive Payments by Wire offer is hidden while a payment is approved or on its way."
                              : "They see a Receive Payments by Wire button and a one-time popup; using it opens a Client Override for wire entry and emails you. They can cancel it and keep ACH."}
                          </Text>
                        )}
                      </>
                    ) : (
                      <Text fontSize="xs" color="gray.700">
                        {isAllPaid && !manualEntryOverride
                          ? "No bank connected, and no connect form is shown — the deal is fully paid out."
                          : isForeign
                            ? "No bank connected — they see the international (SWIFT/IBAN) form."
                            : "No bank connected — they see the US Plaid connect form. A staged pending amount submits automatically when they connect."}
                      </Text>
                    )}
                    {manualEntryOverride && (
                      <Text fontSize="xs" color="orange.700">
                        Client Override is ACTIVE — the manual re-entry form is open for them.
                      </Text>
                    )}
                  </Stack>
                )}

                {/* DM-only: stage & submit a payment for this deal (same flow + controls as OG Set Pending Liquidity) */}
                {isViewingAsClient && (
                  <DmSubmitPanel
                    currentDeal={currentDeal}
                    hasConnectedBank={hasConnectedBank}
                    hasPendingPayment={hasPendingPayment}
                    onSubmitted={refreshPaymentData}
                  />
                )}

                {/* Bank connection forms — visible at top when no bank connected (clients only),
                    or whenever the Client Override switch is on (replace wrong details) */}
                {/* dataCleared re-opens the form for a client who just disconnected: isAllPaid
                    otherwise suppresses it, which would strand them with no bank and no way
                    to add one — worse than the button not working at all. */}
                {!isViewingAsClient && !bankAccountsLoading && ((!hasConnectedBank && (!isAllPaid || dataCleared)) || manualEntryOverride || wireEntryRequested || replaceMode) && (
                  <>
                    <div ref={bankFormRef} />
                    {(manualEntryOverride || wireEntryRequested || replaceMode) && hasConnectedBank && (
                      <Stack gap={1}>
                        {/* Wire mode reads in gray.800: this theme's orange scale is inverted,
                            and the orange.600 used for the replace banner is barely legible. */}
                        <Text fontSize="sm" color={wireEntryMode ? "gray.800" : "orange.600"} fontWeight="semibold">
                          {wireEntryMode
                            ? "Please enter your bank's wire instructions below. Once saved, they will replace your ACH connection as the account we pay."
                            : "Re-enter your bank details below — submitting will replace your currently connected account as the payment destination."}
                        </Text>
                        {wireEntryMode && (
                          <Button variant="ghost" size="xs" w="fit-content" onClick={handleCancelWireEntry} loading={wireCancelLoading} disabled={wireCancelLoading}>
                            Cancel and Keep ACH
                          </Button>
                        )}
                        {wireEntryMode && wireRequestError && (
                          <Text fontSize="xs" color="red.600">{wireRequestError}</Text>
                        )}
                        {replaceMode && (
                          <Button variant="ghost" size="xs" w="fit-content" onClick={() => setReplaceMode(false)}>
                            Never mind — keep my current account
                          </Button>
                        )}
                      </Stack>
                    )}
                    {/* Test user gets manual toggle; everyone else is auto-routed */}
                    {isTestUser && (
                      <Stack direction={{ base: "column", md: "row" }} gap={3}>
                        <Button
                          variant={selectedMethod === "us" ? "solid" : "outline"}
                          onClick={() => setSelectedMethod("us")}
                          flex={1}
                        >
                          US Bank Account
                        </Button>
                        <Button
                          variant={selectedMethod === "foreign" ? "solid" : "outline"}
                          onClick={() => setSelectedMethod("foreign")}
                          flex={1}
                        >
                          Foreign Bank Account
                        </Button>
                      </Stack>
                    )}

                    {/* Render selected payment form.
                        Foreign clients skip Plaid entirely — Plaid cannot connect a
                        non-US bank, so there is no verification to gate on. Everyone
                        else keeps the Plaid-first flow: manual entry unlocks after a
                        Plaid failure (handlePlaidFailure) or a DM Client Override.
                        The one always-open door is the "bank outside the US" escape
                        below — for those clients manual entry is the ONLY path, and
                        gating it behind a Plaid attempt they cannot complete was the
                        dead end international clients kept hitting. */}
                    {selectedMethod === "foreign" ? (
                      <>
                        <Separator />
                        <Text fontSize="lg" fontWeight="semibold">
                          Bank Account Payment Center
                        </Text>
                        <ManualBankFormComponent
                          defaultClientData={defaultClientData}
                          dealEsoId={currentDeal?.esoId}
                          dealManagerEmail={currentDeal?.dealManager?.email}
                          paymentAmount={paymentAmount}
                          paymentLabel={paymentLabel}
                          detectedCountry={paymentsClientCountry}
                          onSuccess={handlePaymentSuccess}
                        />
                      </>
                    ) : (
                      <>
                        <Separator />
                        <Text fontSize="lg" fontWeight="semibold">
                          Bank Account Payment Center
                        </Text>
                        {(isTestUser || manualBankUnlocked) && !wireEntryMode && (
                          <Stack direction={{ base: "column", md: "row" }} gap={3}>
                            <Button
                              variant={usSubMethod === "plaid" || usSubMethod === null ? "solid" : "outline"}
                              onClick={() => setUsSubMethod("plaid")}
                              flex={1}
                            >
                              Connect Bank (Plaid)
                            </Button>
                            <Button
                              variant={usSubMethod === "manual" ? "solid" : "outline"}
                              onClick={() => setUsSubMethod("manual")}
                              flex={1}
                            >
                              Connect Bank (Without Plaid)
                            </Button>
                          </Stack>
                        )}
                        {(usSubMethod === "plaid" || usSubMethod === null) && !wireEntryMode && (
                          <>
                            <UsPlaidWireInformationComponent
                              defaultClientData={defaultClientData}
                              dealEsoId={currentDeal?.esoId}
                              paymentAmount={paymentAmount}
                              paymentLabel={paymentLabel}
                              onSuccess={handlePaymentSuccess}
                              onPlaidFailure={handlePlaidFailure}
                            />
                            <Button
                              variant="outline"
                              w="fit-content"
                              onClick={() => {
                                setManualCountryHint(paymentsClientCountry ?? "")
                                setPromptForCountry(true)
                                setUsSubMethod("manual")
                              }}
                            >
                              My bank is outside the United States
                            </Button>
                          </>
                        )}
                        {(usSubMethod === "manual" || wireEntryMode) && (
                          <ManualBankFormComponent
                            defaultClientData={defaultClientData}
                            dealEsoId={currentDeal?.esoId}
                            dealManagerEmail={currentDeal?.dealManager?.email}
                            paymentAmount={paymentAmount}
                            paymentLabel={paymentLabel}
                            detectedCountry={wireEntryMode ? "US" : (manualCountryHint || paymentsClientCountry)}
                            promptForCountry={wireEntryMode ? false : promptForCountry}
                            wireMode={wireEntryMode}
                            onSuccess={handlePaymentSuccess}
                          />
                        )}
                      </>
                    )}
                  </>
                )}

                {/* Client ID error — the client's portal record must carry an esoId */}
                {clientIdError && (
                  <Stack p={4} borderWidth="1px" borderRadius="lg" borderColor="red.200" bg="red.50">
                    <Text fontSize="sm" fontWeight="semibold" color="red.700">
                      Client ID Error!
                    </Text>
                    <Text fontSize="xs" color="red.600">
                      {isViewingAsClient
                        ? "This client's portal record is missing an ESO client ID (the OG client-create sets it). Re-run the OG client create for this client, or contact engineering."
                        : "Your account is missing an ESO client ID. Please contact your equity partner to resolve this before proceeding with payments."}
                    </Text>
                  </Stack>
                )}

                <Separator />

                {sortedDeals.map((deal, index) => {
                  const dealPending = deal.pendingAmount ?? 0
                  return (
                    <Stack key={deal.id} gap={6}>
                      {index > 0 && <Separator />}
                      <DealSummarySection currentDeal={deal} releasedTotal={0} />
                      {dealPending > 0 && (
                        <Stack p={5} borderWidth="1px" borderRadius="lg" borderColor="orange.200" bg="orange.50">
                          <Text fontSize="sm" color="gray.600">
                            Pending Payment from ESO Fund
                          </Text>
                          <Text fontSize="3xl" fontWeight="extrabold" color="orange.700">
                            {new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 }).format(dealPending)}
                          </Text>
                          {deal.pendingDescription && (
                            <Text fontSize="sm" color="gray.500">{deal.pendingDescription}</Text>
                          )}
                          {/* No client acceptance — submission is automatic. Show status only. */}
                          {hasConnectedBank ? (
                            <HStack mt={2} gap={2} alignItems="center">
                              <LuClock size={14} color="var(--chakra-colors-gray-500)" />
                              <Text fontSize="sm" color="gray.600" fontWeight="medium">
                                {hasPendingPayment
                                  ? "Submitted to ESO Fund — pending approval. No action needed; you'll be notified when funds are released."
                                  : "We're processing this payment — no action needed on your part."}
                              </Text>
                            </HStack>
                          ) : (
                            <Text fontSize="sm" color="gray.600" mt={2}>
                              Connect your bank account below to receive this payment. Once connected, it's sent to ESO Fund for approval automatically — no further action needed.
                            </Text>
                          )}
                        </Stack>
                      )}
                    </Stack>
                  )
                })}

                {/* Payment info banner */}
                {!isAllPaid && (
                  <Stack p={5} borderWidth="1px" borderRadius="lg" borderColor="gray.200" bg="gray.50" gap={4}>
                    <HStack gap={2} alignItems="center">
                      <LuInfo size={18} color="var(--chakra-colors-blue-600)" />
                      <Text fontSize="md" fontWeight="semibold" color="gray.800">
                        When will I receive my payment?
                      </Text>
                    </HStack>

                    <Text fontSize="sm" color="gray.600" lineHeight="tall">
                      {releasedTotal > 0
                        ? "Your ESO Fund equity partner is preparing your next payment. Please provide proof of your stock certificate in order to receive it."
                        : "Your ESO Fund equity partner is preparing your initial payment for you."
                      }
                    </Text>

                    <HStack gap={2} alignItems="flex-start">
                      <Box mt="2px">
                        <LuClock size={14} color="var(--chakra-colors-gray-500)" />
                      </Box>
                      <Text fontSize="sm" color="gray.500" lineHeight="tall">
                        Once your bank account is connected, funds are typically deposited the same day or the next business day. Same-day ACH cuts off at <Text as="span" fontWeight="semibold">1:30 PM Eastern</Text> and wires at <Text as="span" fontWeight="semibold">5:00 PM Eastern</Text>. We'll send your payment by wire whenever your bank supports it.
                      </Text>
                    </HStack>
                  </Stack>
                )}

                <Separator />

                <PaymentLedgerSection cashFlows={cashFlows} paymentHistory={paymentHistory} isLoading={cashFlowsLoading} />

                {/* DM-only: the IRR ledger's view of this deal, for reference (Bill, 2026-10-16). Never shown to the client. */}
                {isViewingAsClient && currentDeal && <IrrReferencePanel currentDeal={currentDeal} />}

              </Stack>
            )}
          </>
        )
      }

      {/* Manual-bank unlock modal — fired when Plaid Link reports an error.
          Reveals the "Connect Bank (Without Plaid)" path for clients whose
          bank doesn't link cleanly through Plaid (e.g. Schwab). */}
      <Dialog.Root open={showManualUnlockModal} onOpenChange={(e) => !e.open && setShowManualUnlockModal(false)}>
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <VStack p={10} gap={4}>
                <Text fontSize="2xl" fontWeight="bold" textAlign="center">Manual Bank Entry Enabled</Text>
                <Text textAlign="center" color="gray.700">
                  {manualUnlockReason || "We weren\u2019t able to connect your bank through Plaid."}
                </Text>
                <Text textAlign="center" color="gray.700">
                  This sometimes happens with certain institutions (Schwab is the most common). We\u2019ve enabled the
                  &ldquo;<strong>Connect Bank (Without Plaid)</strong>&rdquo; option for you so you can enter your routing and account number directly.
                </Text>
                <Text textAlign="center" fontSize="sm" color="gray.500">
                  You can still try Plaid again if you\u2019d like &mdash; both options are available now.
                </Text>
                <Button mt={3} variant="solid" onClick={() => { setShowManualUnlockModal(false); setUsSubMethod("manual") }}>
                  Use Manual Bank Entry
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setShowManualUnlockModal(false)}>
                  Stay on Plaid for now
                </Button>
              </VStack>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>

      {/* "Receive Payments by Wire" popup: once per ACH-only account (the button in
          the bank box stays). Copy is client-facing: "equity partner", no DM. */}
      <Dialog.Root open={wireOfferOpen} onOpenChange={(e) => { if (!e.open) dismissWireOffer() }}>
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <VStack p={10} gap={4}>
                <Box borderRadius={5} bg="blue.500" p={3}>
                  <LuLandmark size={28} color="white" />
                </Box>
                <Text fontSize="2xl" fontWeight="bold" textAlign="center">Receive Your Payments by Wire?</Text>
                <Text textAlign="center" color="gray.700">
                  Your {wireOfferBankLabel} is connected for ACH transfers only. This is a limitation of
                  Plaid, the service we use to connect bank accounts: for certain banks and account types,
                  Plaid provides only the details needed for ACH, not those required for a wire transfer.
                </Text>
                <Text textAlign="center" color="gray.700">
                  ACH transfers typically take longer to arrive. If you would prefer to receive your payments
                  by wire, you can provide your bank&rsquo;s wire instructions. These are usually available in
                  your bank&rsquo;s app or website under Wire Transfers or Account Details.
                </Text>
                <Text textAlign="center" fontSize="sm" color="gray.500">
                  Your equity partner will be notified of this request.
                </Text>
                <Button mt={3} variant="solid" onClick={handleRequestWireEntry} loading={wireRequestLoading} disabled={wireRequestLoading}>
                  Provide Wire Instructions
                </Button>
                <Button variant="ghost" size="sm" onClick={dismissWireOffer} disabled={wireRequestLoading}>
                  Continue with ACH
                </Button>
                {wireRequestError && (
                  <Text fontSize="sm" color="red.600" textAlign="center">{wireRequestError}</Text>
                )}
              </VStack>
              <Dialog.CloseTrigger asChild position="absolute" top={3} right={3}>
                <CloseButton size="sm" />
              </Dialog.CloseTrigger>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>

      {/* Clear data confirmation modal */}
      <Dialog.Root open={showClearModal} onOpenChange={(e) => !e.open && setShowClearModal(false)}>
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <VStack p={10} gap={5}>
                <Box borderRadius={5} bg="red.400" p={3}>
                  <TiWarningOutline size={30} color="white" />
                </Box>
                <Text fontSize="2xl" fontWeight="bold">Disconnect Bank Account?</Text>
                <Text textAlign="center" color="gray.600">
                  This {isTestUser ? "test " : ""}account will stop receiving payments and you can connect a different one afterwards. Your payment history is kept.
                </Text>
                <Text textAlign="center" fontSize="xs" color="gray.500">
                  We&rsquo;ll also ask Plaid to remove the ESO Fund connection from your bank&rsquo;s connected apps.
                </Text>
                <HStack mt={5}>
                  <Button
                    variant="solid"
                    bg="red.500"
                    onClick={handleClearTestData}
                    loading={isClearing}
                    disabled={isClearing}
                  >
                    Yes, Disconnect
                  </Button>
                  <Button variant="outline" onClick={() => setShowClearModal(false)} disabled={isClearing}>
                    Cancel
                  </Button>
                </HStack>
              </VStack>
              <Dialog.CloseTrigger asChild position="absolute" top={3} right={3}>
                <CloseButton size="sm" />
              </Dialog.CloseTrigger>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>

      {/* Payment success modal */}
      <Dialog.Root open={showSuccessModal} onOpenChange={(e) => { if (!e.open) handleSuccessModalClose() }}>
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content overflow="hidden">
              <Box position="absolute" top={0} left={0} w="100%" h="100%" pointerEvents="none" zIndex={0}>
                <ConfettiBackground duration={4000} initialPieces={150} frozenPieces={0} />
              </Box>
              <VStack p={10} gap={5} position="relative" zIndex={1}>
                <Box borderRadius={5} bg="green.400" p={3}>
                  <FaCheckCircle size={30} color="white" />
                </Box>
                <Text fontSize="2xl" fontWeight="bold">{successTitle}</Text>
                <Text textAlign="center" color="gray.600">
                  {successMessage}
                </Text>
                <Button
                  variant="solid"
                  colorPalette="green"
                  mt={3}
                  onClick={handleSuccessModalClose}
                >
                  Continue
                </Button>
              </VStack>
              <Dialog.CloseTrigger asChild position="absolute" top={3} right={3}>
                <CloseButton size="sm" />
              </Dialog.CloseTrigger>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    </Section>
  )
}
